import { loadImage } from "../../UIController/imageloader";
import { getAsString } from "../../UIController/stars";
import { formatTime, lowercaseFirstLetter, showModal } from "../../Util/helper";
import { getTags } from "../../UIController/tags";
import { updateSelection } from "../../UIController/multiSelector";

export function createReviewCard(review, list, skeleton, onClick) {
    if (typeof list === 'string') {
        list = document.getElementById(list);
    }

    if (typeof skeleton === 'string') {
        skeleton = document.getElementById(skeleton);
    }

    if (!skeleton || !list) {
        console.error('Invalid list or skeleton element');
        return;
    }

    let baseId = 'review-list-' + review.id;
    const originalBaseId = baseId;

    if (document.getElementById(baseId)) {
        const elements = document.getElementsByClassName(originalBaseId);
        const count = elements.length;

        for (let i = 0; i < count; i++) {
            const element = elements[i];

            if (!document.getElementById(baseId + '-' + i)) {
                baseId = baseId + '-' + i;
                break;
            }

            if (element.parentElement == list) {
                return; // Element already exists in the list
            }
        }

        baseId = baseId + '-' + count;
    }

    const carouselItem = document.createElement('div');
    carouselItem.classList.add('carousel-view-item', originalBaseId, 'border-2', 'rounded-lg', 'border-transparent', 'w-64');
    carouselItem.id = baseId;

    if (onClick && typeof onClick === 'function') {
        carouselItem.addEventListener('click', onClick);
    }
    
    const reviewElement = document.createElement('div');
    reviewElement.classList.add('card', 'shadow-lg', 'bg-base-100', 'rounded-lg', 'w-full');
    
    const avgRating = ((review.avgAmbiance + review.avgIngredients + review.avgPresentation + 
                    review.avgSauce + review.avgTaste + review.avgValue) / 6).toFixed(2);

    reviewElement.innerHTML = `
        <a id='${baseId}-wrapper' class="relative h-28 w-full cursor-pointer" href='/r/${review.id}'>
            <img id='${baseId}-img' 
                alt="Review Thumbnail" 
                class="rounded-lg mb-3 h-28 w-full object-cover transition-opacity duration-300">
            <span class="text-sm ml-2 badge badge-primary absolute top-1 left-0 p-0" style="gap: 0; padding: 2px;"><i class="fa-solid fa-star-half-stroke mx-1"></i> ${avgRating} (${review.totalRatings})</span>
            <span class="text-sm mr-2 badge badge-secondary absolute top-1 right-0 p-0" style="gap: 0; padding: 2px;">${review.price}€</span>
        </a>
        <a href="${review.locationUrl}" class="text-sm text-gray-500 ml-2 mr-2"><i class="fa-solid fa-location-dot"></i> ${review.street} ${review.number}, ${review.cityName || 'Unknown City'}</a>
        <a href="/r/${review.id}" class="font-bold text-lg block truncate ml-2">${review.name}</a>
        <div class="overflow-x-auto overflow-y-hidden gap-2 flex flex-row mb-2"></div>
    `;

    const badgeContainer = reviewElement.querySelector('div.overflow-x-auto');
    const badgeList = getTags(review, (text, id) => {
        if (id != 'cheap') {
            const checkbox = document.getElementById(id);

            if (checkbox) {
                checkbox.checked = !checkbox.checked;
                updateSelection(checkbox);
            } else {
                console.warn(`Checkbox with id '${id}' not found.`);
            }
        } else {
            priceUpdate(0);
        }
    });

    badgeList.forEach(badge => {
        badgeContainer.appendChild(badge);
    });

    if (document.getElementById('ratingCalcModal')) {
        const infoBtn = document.createElement('button');
        infoBtn.type = 'button';
        infoBtn.classList.add('btn', 'btn-primary', 'btn-ghost', 'btn-circle', 'btn-xs');
        const icon = document.createElement('i');
        icon.classList.add('fa-solid', 'fa-circle-info');
        infoBtn.appendChild(icon);
        infoBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            showModal('ratingCalcModal', review);
        });
        const ratingValue = reviewElement.querySelector('span.badge-primary');
        ratingValue.appendChild(infoBtn);
    }

    carouselItem.appendChild(reviewElement);
    // Save the current scroll position
    const scrollLeft = list.scrollLeft;

    if (skeleton) {
        list.insertBefore(carouselItem, skeleton);
    } else {
        list.appendChild(carouselItem);
    }
    
    if (review.images.length === 0) {
        review.images = [{ url: '/img/placeholder.png' }];
    }

    loadImage(review.images[0].url, document.getElementById(baseId + '-img'), document.getElementById(baseId + '-wrapper'), {
        fallbackUrl: '/img/placeholder.png'
    });

    // Restore the scroll position
    list.scrollLeft = scrollLeft;
}

export function createRatingCard(rating, list, skeleton) {
    const carouselItem = document.createElement('div');
    carouselItem.classList.add('carousel-item');
    const avgRating = ((rating.rating.ambiance + rating.rating.ingredients + rating.rating.presentation + rating.rating.sauce + rating.rating.taste + rating.rating.value) / 6).toFixed(2);
    const ratingElement = document.createElement('div');
    
    // Adjust height to accommodate comment and images
    ratingElement.classList.add('card', 'shadow-lg', 'p-4', 'bg-base-100', 'rounded-lg', 'w-64', 'h-auto', 'cursor-pointer');
    
    const addedDate = new Date(rating.rating.updatedAt ? rating.rating.updatedAt : rating.rating.createdAt);
    const humanReadableDate = formatTime(addedDate);
    
    // Truncate comment if exists
    const maxCommentLength = 100;
    let commentHTML = `<p class="badge badge-accent my-3">${t('rating.noComment')}</p>`;
    if (rating.rating.comment) {
        const text = (rating.rating.comment.original? rating.rating.comment[rating.rating.comment.original] : rating.rating.comment[locale]) || rating.rating.comment['en'] || '';

        const truncatedComment = text.length > maxCommentLength 
            ? text.substring(0, maxCommentLength) + '...' 
            : text;
        commentHTML = `<p class="text-sm text-gray-600 dark:text-base-content my-3 italic">${truncatedComment}</p>`;
    }
    
    // Show image count badge if there are rating images
    let imageBadgeHTML = '';
    if (rating.rating.images && rating.rating.images.length > 0) {
        imageBadgeHTML = `<span class="badge badge-sm badge-primary ml-2">${rating.rating.images.length} <i class="fa-solid fa-image ml-1"></i></span>`;
    }
    
    ratingElement.innerHTML = `
        <div class="flex justify-between">
            <a class="relative w-12 h-12 mb-3" href="/r/${rating.review.id}">
                <img id="rating-image-${rating.review.id}" alt="Rating Thumbnail" class="rounded-lg size-12 object-cover cursor-pointer">
            </a>
            <a href="/r/${rating.review.id}?modal=ratingModal" class="btn btn-ghost btn-circle"><i class="fa-solid fa-pen"></i></a>
        </div>
        <a class="font-bold text-lg block truncate" href="/r/${rating.review.id}">${rating.review.name}</a>
        <p class="text-sm text-gray-500">${humanReadableDate}${imageBadgeHTML}</p>
        ${commentHTML}
        <a class="flex items-center space-x-2" href="/r/${rating.review.id}?modal=ratingModal">
            <div id="rating-${rating.review.id}">${getAsString(avgRating)}</div>
            <span class="text-sm" id="rating-value-${rating.review.id}">(${avgRating})</span>
        </a>
    `;

    const scrollLeft = list.scrollLeft;

    carouselItem.appendChild(ratingElement);
        
    if (skeleton) {
        list.insertBefore(carouselItem, skeleton);
    } else {
        list.appendChild(carouselItem);
    }

    loadImage(rating.rating.images && rating.rating.images.length > 0 ? rating.rating.images[0].url : null, 
        document.getElementById(`rating-image-${rating.review.id}`),
        null,
        { classes: ['size-12', 'rounded-lg'], fallbackUrl: '/img/placeholder.png' }
    );   

    list.scrollLeft = scrollLeft;
}